import { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import {
  Home,
  FolderOpen,
  Sprout,
  CreditCard,
  ChevronDown,
  Menu,
  Users,
  BookOpen,
  PawPrint,
  UserCheck,
  Milk,
  Calendar,
  ShoppingCart,
  History,
  Receipt,
  FileText,
  Shield,
  Settings,
  Store,
  BookMarked,
  Wallet,
  Landmark,
  LayoutDashboard
} from 'lucide-react';
import './sidebar.css';

function Sidebar({ onNavigate }) {
  const [activeMenu, setActiveMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions, hasPermission } = useContext(AuthContext);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
    if (!collapsed) setActiveMenu(null);
  };

  const toggleMenu = (menuId) => {
    if (collapsed) return;
    setActiveMenu((prev) => (prev === menuId ? null : menuId));
  };

  const navigateTo = (page) => {
    const route = page.toLowerCase();
    navigate(`/${route}`);
    if (onNavigate) {
      onNavigate(page.charAt(0).toUpperCase() + page.slice(1));
    }
    
    // Auto-close sidebar on mobile after navigation
    if (window.innerWidth <= 768) {
      setCollapsed(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only auto-close on mobile
      if (window.innerWidth <= 768) {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
          // Don't close if clicking the mobile menu button
          if (!event.target.closest('.mobile-menu-btn')) {
            setCollapsed(true);
            setActiveMenu(null);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const path = location.pathname.slice(1); // Remove leading slash
    
    // Define the correct mapping for each menu section
    const portfolioItems = ['tabela', 'khata', 'employees', 'animals', 'customers', 'vendors', 'dairy-farm'];
    const produceItems = ['daily-khata-milk', 'animal-record'];
    const accountingItems = ['milk-purchase', 'goods-purchase', 'sales', 'receipt', 'payment-history', 'animal-purchase', 'cashbook', 'bank-statement'];
    const billingItems = ['bill-generator', 'party-ledger'];
    const adminItems = ['admin'];

    // Set active menu based on current path
    if (portfolioItems.includes(path)) {
      setActiveMenu('portfolio');
    } else if (produceItems.includes(path)) {
      setActiveMenu('produce');
    } else if (accountingItems.includes(path)) {
      setActiveMenu('accounting');
    } else if (billingItems.includes(path)) {
      setActiveMenu('billing');
    } else if (adminItems.includes(path)) {
      setActiveMenu('admin_menu');
    } else {
      setActiveMenu(null);
    }
  }, [location.pathname]);

  // Filter menu items based on permissions
  const getFilteredMenuItems = (menuItems, section) => {
    return menuItems.filter(item => {
      if (item.toLowerCase() === 'admin' && user?.role === 'admin') return true;
      const itemKey = item.toLowerCase().replace(/\-/g, '-');
      return hasPermission && hasPermission(section, itemKey);
    });
  };

  const menuList = [
    {
      id: 'portfolio',
      icon: FolderOpen,
      label: 'Portfolio',
      section: 'portfolio',
      items: ['Tabela', 'Khata', 'Employees', 'Animals', 'Customers', 'Vendors', 'Dairy-Farm']
    },
    {
      id: 'produce',
      icon: Sprout,
      label: 'Produce',
      section: 'produce',
      items: ['Daily-Khata-Milk', 'Animal-Record']
    },
    {
      id: 'billing',
      icon: CreditCard,
      label: 'Billing',
      section: 'billing',
      items: ['Bill-Generator', 'Party-Ledger']
    },
    {
      id: 'accounting',
      icon: CreditCard,
      label: 'Accounting',
      section: 'accounting',
      items: ['Milk-Purchase', 'Goods-Purchase', 'Animal-Purchase', 'Sales', 'Receipt', 'Payment-History', 'Cashbook', 'Bank-Statement']
    },
    ...(user?.role === 'owner' ? [{
      id: 'settings_menu',
      icon: Settings,
      label: 'Settings',
      section: 'portfolio',
      items: ['Settings']
    }] : []),
    ...(user?.role === 'admin' ? [{
      id: 'admin_menu',
      icon: Shield,
      label: 'God Mode',
      section: 'admin',
      items: ['Admin']
    }] : [])
  ];

  // Filter menus based on permissions
  const filteredMenuList = menuList.filter(menu => {
    // Show Accounting only for owners/admins
    if (menu.id === 'accounting' && user?.role !== 'owner' && user?.role !== 'admin') {
        return false;
    }
    const filteredItems = getFilteredMenuItems(menu.items, menu.section);
    return filteredItems.length > 0;
  }).map(menu => ({
    ...menu,
    items: getFilteredMenuItems(menu.items, menu.section)
  }));

  // Safety check for user and permissions
  if (!user || !permissions) {
    return (
      <>
        {/* Mobile Menu Button */}
        <button className="mobile-menu-btn" onClick={toggleSidebar}>
          <Menu size={24} />
        </button>
        
        <nav ref={sidebarRef} className={`sidebar ${collapsed ? 'collapsed' : ''}`} id="sidebar">
          <div className="sidebar-header">
            <h2 className="sidebar-title">Loading...</h2>
            <button className="toggle-btn" onClick={toggleSidebar}><Menu size={20} /></button>
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      {/* Mobile Menu Button - Always visible on mobile */}
      <button className="mobile-menu-btn" onClick={toggleSidebar}>
        <Menu size={24} />
      </button>
      
      <nav ref={sidebarRef} className={`sidebar ${collapsed ? 'collapsed' : ''}`} id="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand" onClick={() => navigateTo('Home')}>
            <div className="brand-icon">
              <Milk size={24} color="var(--primary)" />
            </div>
            <h2 className="sidebar-title">Dairy<span className="text-primary">Farm</span></h2>
          </div>
          <button className="toggle-btn" onClick={toggleSidebar}>
            <Menu size={20} />
          </button>
        </div>
        
        <ul className="sidebar-menu">
          <li className="menu-item">
             <div
                className={`menu-button single-link ${location.pathname === '/home' ? 'active' : ''}`}
                onClick={() => navigateTo('Home')}
             >
                <span className="menu-content">
                  <span className="menu-icon"><Home size={20} /></span>
                  <span className="menu-text">Home</span>
                </span>
             </div>
             <div className="tooltip">Home</div>
          </li>

          {user?.role === 'owner' && (
            <li className="menu-item">
               <div
                  className={`menu-button single-link ${location.pathname === '/owner-dashboard' ? 'active' : ''}`}
                  onClick={() => navigateTo('owner-dashboard')}
               >
                  <span className="menu-content">
                    <span className="menu-icon"><LayoutDashboard size={20} /></span>
                    <span className="menu-text">Owner Dashboard</span>
                  </span>
               </div>
               <div className="tooltip">Owner Dashboard</div>
            </li>
          )}

          {filteredMenuList.map((menu) => {
            const MenuIcon = menu.icon;
            return (
            <li className="menu-item" key={menu.id}>
              <button
                className={`menu-button ${activeMenu === menu.id ? 'active expanded' : ''}`}
                onClick={() => toggleMenu(menu.id)}
              >
                <span className="menu-content">
                  <span className="menu-icon"><MenuIcon size={20} /></span>
                  <span className="menu-text">{menu.label}</span>
                </span>
                <span className="expand-icon"><ChevronDown size={16} /></span>
              </button>
              <div className="tooltip">{menu.label}</div>
              <div className={`submenu ${activeMenu === menu.id ? 'expanded' : ''}`}>
                {menu.items.map((item) => {
                  const itemPath = `/${item.toLowerCase()}`;
                  const isActiveItem = location.pathname === itemPath;
                  
                  // Icon mapping for submenu items
                  let ItemIcon = FileText;
                  if (item === 'Admin') ItemIcon = Shield;
                  if (item === 'Tabela') ItemIcon = Home;
                  if (item === 'Khata') ItemIcon = BookOpen;
                  if (item === 'Employees') ItemIcon = Users;
                  if (item === 'Animals') ItemIcon = PawPrint;
                  if (item === 'Customers') ItemIcon = UserCheck;
                  if (item === 'Daily-Khata-Milk') ItemIcon = Milk;
                  if (item === 'Animal-Record') ItemIcon = Calendar;
                  if (item === 'Milk-Purchase') ItemIcon = ShoppingCart;
                  if (item === 'Animal-Purchase') ItemIcon = PawPrint;
                  if (item === 'Selling-History') ItemIcon = History;
                  if (item === 'Payment-History') ItemIcon = Receipt;
                  if (item === 'Vendors') ItemIcon = Store;
                  if (item === 'Party-Ledger') ItemIcon = BookMarked;
                  if (item === 'Cashbook') ItemIcon = Wallet;
                  if (item === 'Bank-Statement') ItemIcon = Landmark;
                  if (item === 'Journal-Entry') ItemIcon = BookOpen;
                  
                  return (
                    <div
                      key={item}
                      className={`submenu-item ${isActiveItem ? 'active' : ''}`}
                      onClick={() => navigateTo(item.toLowerCase())}
                    >
                      <span className="submenu-icon"><ItemIcon size={16} /></span>
                      <span className="submenu-text">
                        {item === 'Daily-Khata-Milk' ? 'Daily Khata Milk' :
                         item === 'Animal-Record' ? 'Animal Record' :
                         item === 'Milk-Purchase' ? 'Milk Purchase' :
                         item === 'Animal-Purchase' ? 'Animal Purchase' :
                         item === 'Goods-Purchase' ? 'Goods Purchase' :
                         item === 'Selling-History' ? 'Selling History' :
                         item === 'Payment-History' ? 'Payment History' :
                         item === 'Party-Ledger' ? 'Party Ledger' :
                         item === 'Bank-Statement' ? 'Bank Statement' :
                         item === 'Journal-Entry' ? 'Journal Entry' :
                         item.replace(/-/g, ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </li>
          )})}
        </ul>
        
        {/* Permission Debug Info (only in development and when explicitly enabled) */}
        {(process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG === 'true') && (
          <div className="debug-info">
            <details>
              <summary>Debug Info</summary>
              <div>
                <strong>User:</strong> {user?.name} ({user?.role})
              </div>
              <div>
                <strong>Permissions:</strong>
              </div>
              <pre>{JSON.stringify(permissions, null, 2)}</pre>
            </details>
          </div>
        )}
      </nav>
    </>
  );
}

export default Sidebar;